import axios from 'axios';
import { STUDENT_TOKEN, MESSAGES_PER_PAGE } from '../../constants';

const API_URL = process.env.REACT_APP_API_URL;

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_URL,
});

// Create a separate instance for public endpoints
const publicApi = axios.create({
  baseURL: API_URL,
});

// Add a request interceptor to include the token in all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(STUDENT_TOKEN);
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

export const studentApi = {
  requestMagicLink: async (email) => {
    try {
      const response = await publicApi.post('/api/student/request-magic-link/', { email });
      return response.data;
    } catch (error) {
      console.error('Request magic link error:', error.response?.data || error.message);
      throw error;
    }
  },

  getStudentInfo: async () => {
    try {
      const response = await api.get('/api/student/profile/');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching student info:', error.response?.data || error.message);
      throw error;
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/api/student/profile/', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error.response?.data || error.message);
      throw error;
    }
  },

  getChatrooms: async () => {
    try {
      const response = await api.get('/api/chats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching chatrooms:', error.response?.data || error.message);
      throw error;
    }
  },

  getChatroomMessages: async (chatroomId, limit = MESSAGES_PER_PAGE, offset = 0) => {
    try {
      // Construct URL with query parameters manually to ensure they're included
      const url = `/api/chats/${chatroomId}/messages/?limit=${limit}&offset=${offset}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error.response?.data || error.message);
      throw error;
    }
  },

  sendMessage: async (chatroomId, content) => {
    try {
      const response = await api.post(`/api/chats/${chatroomId}/send_message/`, {
        content: content
      });
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error.response?.data || error.message);
      throw error;
    }
  },

  markMessagesAsRead: async (chatroomId) => {
    try {
      const response = await api.post(`/api/chats/${chatroomId}/mark_as_read/`);
      return response.data;
    } catch (error) {
      console.error('Error marking messages as read:', error.response?.data || error.message);
      throw error;
    }
  },

  getUnreadMessages: async (chatroomId) => {
    try {
      const response = await api.get(`/api/chats/${chatroomId}/unread_messages/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching unread messages:', error.response?.data || error.message);
      throw error;
    }
  },

  submitEnquiry: async (formData, token = null) => {
    console.log(token);
    try {
      // Use the authenticated api instance if token exists, otherwise use publicApi
      const apiInstance = token ? api : publicApi;
      
      const response = await apiInstance.post('/api/student/enquiry/', formData);
      return response.data.data;
    } catch (error) {
      console.error('Enquiry submission error:', error.response?.data || error.message);
      throw error;
    }
  },

  scheduleDemo: async (chatroomId, scheduleData) => {
    try {
      const response = await api.post(`/api/schedule-demo/`, {
        date: scheduleData.date,
        time: scheduleData.time,
        timezone: scheduleData.timezone,
        chatroom_id: chatroomId
      });
      return response.data;
    } catch (error) {
      console.error('Error scheduling demo:', error.response?.data || error.message);
      throw error;
    }
  },

  // Booking APIs
  getBookingTutors: async () => {
    try {
      const response = await api.get('/api/student/bookings/tutors/');
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch tutors');
    } catch (error) {
      console.error('Error fetching booking tutors:', error.response?.data || error.message);
      throw error;
    }
  },

  getTutorAvailableWindows: async (tutorId) => {
    try {
      const response = await api.get(`/api/student/bookings/tutors/${tutorId}/windows/`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch available windows');
    } catch (error) {
      console.error('Error fetching available windows:', error.response?.data || error.message);
      throw error;
    }
  },

  getBookings: async (status = null) => {
    try {
      const params = status ? { status } : {};
      const response = await api.get('/api/student/bookings/', { params });
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch bookings');
    } catch (error) {
      console.error('Error fetching bookings:', error.response?.data || error.message);
      throw error;
    }
  },

  createBooking: async (bookingData) => {
    try {
      const response = await api.post('/api/student/bookings/', bookingData);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create booking');
    } catch (error) {
      console.error('Error creating booking:', error.response?.data || error.message);
      throw error;
    }
  },

  cancelBooking: async (bookingId) => {
    try {
      const response = await api.delete(`/api/student/bookings/${bookingId}/`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to cancel booking');
    } catch (error) {
      console.error('Error cancelling booking:', error.response?.data || error.message);
      throw error;
    }
  },

  getRecordings: async () => {
    try {
      const response = await api.get('/api/student/recordings/');
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch recordings');
    } catch (error) {
      console.error('Error fetching recordings:', error.response?.data || error.message);
      throw error;
    }
  },

  getRecordingDownloadUrl: async (bookingId, s3Key) => {
    try {
      const response = await api.post(`/api/recordings/${bookingId}/download-url/`, { s3_key: s3Key });
      if (response.data.success) {
        return response.data.data.download_url;
      }
      throw new Error(response.data.message || 'Failed to get download URL');
    } catch (error) {
      console.error('Error getting download URL:', error.response?.data || error.message);
      throw error;
    }
  },

  createBookingAttendee: async (bookingId) => {
    try {
      const response = await api.post(`/api/bookings/${bookingId}/attendee/`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create attendee');
    } catch (error) {
      console.error('Error creating attendee:', error.response?.data || error.message);
      throw error;
    }
  },
};

export const getActiveClasses = async () => {
  try {
    const response = await api.get('/api/student/lesson-packs/');
    
    // Check if response.data exists and has data property
    if (!response.data || !response.data.data) {
      console.warn('Unexpected response format:', response);
      return [];
    }

    // Ensure data is an array
    const lessonPacks = Array.isArray(response.data.data) 
      ? response.data.data 
      : [];

    return lessonPacks.map(pack => ({
      id: pack.id,
      studentName: pack.tutor_name,
      subject: pack.subject,
      totalHours: pack.no_of_hours,
      remainingHours: parseFloat(pack.hours_remaining || 0),
      meetingLink: pack.meeting_link,
      status: pack.status,
      expiryDate: pack.expiry_date
    }));
  } catch (error) {
    console.error('Error fetching active classes:', error.response?.data || error.message);
    return []; // Return empty array instead of throwing error
  }
};

export const getInvoices = async (page = 1) => {
  try {
    const response = await api.get('/api/sessions/', {
      params: { page }
    });
    
    const invoices = response.data?.data || response.data?.results || [];
    const pagination = response.data?.pagination || {};
    
    if (!Array.isArray(invoices)) {
      console.error('Invalid response format for invoices:', response.data);
      return { invoices: [], pagination: {} };
    }
    
    return {
      invoices: invoices.map(invoice => ({
        id: invoice.id || '',
        lessonPackId: invoice.lesson_pack || '',
        date: invoice.date || '',
        duration: parseFloat(invoice.duration || 0),
        subject: invoice.subject || '',
        notes: invoice.notes || '',
        createdAt: invoice.created_at || '',
        updatedAt: invoice.updated_at || ''
      })),
      pagination: {
        count: pagination.count || 0,
        next: pagination.next,
        previous: pagination.previous,
        pageSize: pagination.page_size || 20,
        currentPage: page
      }
    };
  } catch (error) {
    console.error('Error fetching invoices:', error.response?.data || error.message);
    throw error;
  }
};

export const getStudentProfile = async () => {
  try {
    const response = await axios.get('/api/student/profile');
    return response.data;
  } catch (error) {
    console.error('Error fetching student profile:', error);
    throw error;
  }
};

export default studentApi;
